import { TableId } from "@greeter/core";
import { queries } from "@greeter/url";

/**
 * Returns empty string if not value is provided
 * otherwise prives the value with a forward slash before it.
 */
function pathIfAny(s?: string): string {
  if (!s) return "";
  return `/${s}`;
}

function tabTemplate(s?: Tabs): string {
  if (!s) return "";
  return `/:tab(${s})`;
}

interface IRouteTemplate {
  template(): string;
}

interface IRoute<TArgs> {
  route(args?: TArgs): string;
}

abstract class Route<TArgs> implements IRoute<TArgs>, IRouteTemplate {
  abstract get basePath(): string;

  route(args?: TArgs): string {
    return this.basePath;
  }

  template(): string {
    return this.basePath;
  }
}

class VenuePath extends Route<void> {
  get basePath() {
    return `/${Tabs.VENUE}`;
  }

  override template() {
    return `/${TabGroup.MAIN}/:tab(${Tabs.VENUE})`;
  }

  override route() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }
}

interface OptionalIdArgs {
  id?: string;
}

interface RequiredIdArgs {
  id: string;
}

interface ProductPathArgs extends OptionalIdArgs {}

class ProductPath extends Route<ProductPathArgs> {
  constructor(private tab?: Tabs) {
    super();
  }

  get basePath() {
    return `/product`;
  }

  override route(args?: ProductPathArgs): string {
    return `/${TabGroup.MAIN}${pathIfAny(this.tab)}${this.basePath}${pathIfAny(
      args?.id
    )}`;
  }

  override template(): string {
    const route = `/${TabGroup.MAIN}${tabTemplate(this.tab)}${
      this.basePath
    }/:id?`;
    return route;
  }
}

class BookingOverviewPath extends Route<void> {
  get basePath(): string {
    return `/booking-overview`;
  }

  override route(): string {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }

  override template(): string {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }
}

class ProductCreatedPath extends Route<RequiredIdArgs> {
  constructor(private tab?: Tabs) {
    super();
  }

  get basePath(): string {
    return "/product-created";
  }

  override route({ id }: RequiredIdArgs): string {
    return `/${TabGroup.MAIN}${pathIfAny(this.tab)}${this.basePath}/${id}`;
  }

  override template(): string {
    return `/${TabGroup.MAIN}${tabTemplate(this.tab)}${this.basePath}/:id`;
  }
}

class EditVenuePath extends Route<void> {
  get basePath() {
    return "/edit-venue";
  }
  override route() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }
  override template() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }
}

type EditEventPathArgs = {
  id: string;
  duplicating?: boolean;
};
class EditEventPath extends Route<EditEventPathArgs> {
  get basePath(): string {
    return "/edit-event";
  }

  override route(args?: EditEventPathArgs): string {
    return `/${TabGroup.MAIN}${this.basePath}${pathIfAny(args?.id)}${queries({
      duplicating: args?.duplicating,
    })}`;
  }

  override template(): string {
    return `/${TabGroup.MAIN}${this.basePath}/:id?`;
  }
}

type EditEventCompletedPathArgs = {
  id: string;
};
class EditEventCompletedPath extends Route<EditEventCompletedPathArgs> {
  get basePath(): string {
    return "/edit-event-completed";
  }
  override route(args: EditEventCompletedPathArgs) {
    return `/${TabGroup.MAIN}${this.basePath}/${args.id}`;
  }

  override template(): string {
    return `/${TabGroup.MAIN}${this.basePath}/:id`;
  }
}

export type CreateBookingPathParams = {
  mode: "create";
  tables?: TableId[];
  productId?: string;
  date?: string;
};

export type UpdateBookingPathParams = {
  mode: "edit";
  bookingId?: string;
};
export type EditBookingPathParams =
  | CreateBookingPathParams
  | UpdateBookingPathParams;

class EditBookingPath extends Route<EditBookingPathParams | undefined> {
  get basePath(): string {
    return "/edit-booking";
  }

  override route(params?: EditBookingPathParams | undefined) {
    return `/${TabGroup.MAIN}${this.basePath}${queries(params)}`;
  }

  override template() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }
}

export type EditBookingCompletedPathParams = {
  id: string;
};
class EditBookingCompletedPath extends Route<EditBookingCompletedPathParams> {
  get basePath(): string {
    return "/edit-booking";
  }

  override template() {
    return `/${TabGroup.MAIN}${this.basePath}/:id/completed`;
  }

  override route(params: EditBookingCompletedPathParams) {
    return `/${TabGroup.MAIN}${this.basePath}/${params.id}/completed`;
  }
}

class LoginPath extends Route<void> {
  get basePath(): string {
    return "/login";
  }

  override route() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }

  override template() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }
}

class TableOverviewPath extends Route<void> {
  get basePath(): string {
    return "/table-overview";
  }

  override route() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }

  override template() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }
}

class TableServiceOverviewPath extends Route<void> {
  get basePath(): string {
    return "/table-service-overview";
  }
  override route() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }

  override template() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }
}

class SettingsPath extends Route<void> {
  get basePath(): string {
    return "/settings";
  }
  override route() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }

  override template() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }
}

class EditMenuPath extends Route<void> {
  get basePath(): string {
    return "/edit-menu";
  }
  override route() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }

  override template() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }
}

export type EditBundlePathParams = {
  bundleId?: string;
};
class EditBundlePath extends Route<EditBundlePathParams> {
  constructor(private tab?: string) {
    super();
  }

  get basePath(): string {
    return `/edit-bundle`;
  }

  get tabPath(): string {
    return this.tab ? `/${this.tab}` : "";
  }

  override template(): string {
    return `/${TabGroup.MAIN}${this.tab ? `/:tab(${this.tab})` : ""}${
      this.basePath
    }/:id?`;
  }

  override route(params: EditBundlePathParams) {
    return `/${TabGroup.MAIN}${this.tabPath}${this.basePath}/${
      params.bundleId ? `${params.bundleId}` : ""
    }`;
  }
}

type EditBundleCompletedParams = {
  bundleId: string;
};
class EditBundleCompletedPath extends Route<EditBundleCompletedParams> {
  constructor(private tab?: string) {
    super();
  }

  get basePath(): string {
    return "/edit-bundle";
  }

  override template(): string {
    return `/${TabGroup.MAIN}${this.tab ? `/:tab(${this.tab})` : ""}${
      this.basePath
    }/:id/completed`;
  }

  override route(params: EditBundleCompletedParams) {
    return `/${TabGroup.MAIN}${this.tab ? `/${this.tab}` : ""}${
      this.basePath
    }/${params.bundleId}/completed`;
  }
}

export type EditTicketEventParams = { id?: string };
class EditTicketEventPath extends Route<EditTicketEventParams> {
  get basePath(): string {
    return "/edit-ticket-event";
  }

  override template(): string {
    return `/${TabGroup.MAIN}${this.basePath}/:id?`;
  }

  override route(params: EditTicketEventParams) {
    return `/${TabGroup.MAIN}${this.basePath}/${params.id}`;
  }
}

class TicketEventOverviewPath extends Route<void> {
  get basePath(): string {
    return "/events";
  }

  override route() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }

  override template() {
    return `/${TabGroup.MAIN}${this.basePath}`;
  }
}

class WardrobePath extends Route<void> {
  constructor(private tab?: Tabs) {
    super();
  }

  get basePath(): string {
    return "/wardrobe";
  }

  override route(): string {
    return `/${TabGroup.MAIN}${pathIfAny(this.tab)}${this.basePath}`;
  }

  override template(): string {
    return `/${TabGroup.MAIN}${tabTemplate(this.tab)}${this.basePath}`;
  }
}

class TicketScannerPath extends Route<void> {
  constructor(private tab: string) {
    super();
  }

  get basePath(): string {
    return "";
  }

  override route(): string {
    return `/${TabGroup.TICKETS}/${this.tab}`;
  }
  override template(): string {
    return `/${TabGroup.TICKETS}/:tab(${this.tab})`;
  }
}

class TicketsPath extends Route<void> {
  constructor(private tab: string) {
    super();
  }

  get basePath(): string {
    return "";
  }

  override route(): string {
    return `/${TabGroup.TICKETS}/${this.tab}`;
  }

  override template(): string {
    return `/${TabGroup.TICKETS}/:tab(${this.tab})`;
  }
}

export enum TabGroup {
  MAIN = "main",
  TICKETS = "tickets",
}

export enum Tabs {
  VENUE = `/venue`,

  SCANNER = `scanner`,
  TICKETS = `guest-list`,
}

export class Routes {
  public static mainGroup = class {
    public static groupName = TabGroup.MAIN;

    public static editBooking = new EditBookingPath();
    public static editBookingCompleted = new EditBookingCompletedPath();
    public static bookingOverview = new BookingOverviewPath();
    public static productCreated = new ProductCreatedPath();
    public static editVenue = new EditVenuePath();
    public static editEvent = new EditEventPath();
    public static ticketEvents = new TicketEventOverviewPath();
    public static editTicketEvent = new EditTicketEventPath();
    public static editEventCompleted = new EditEventCompletedPath();
    public static login = new LoginPath();
    public static tableOverview = new TableOverviewPath();
    public static tableServiceOverview = new TableServiceOverviewPath();
    public static settings = new SettingsPath();
    public static editMenu = new EditMenuPath();
    public static wardrobe = new WardrobePath();

    public static venueTab = class {
      static readonly tabName = Tabs.VENUE;

      public static root = new VenuePath();
      public static wardrobe = new WardrobePath(this.tabName);

      public static product = new ProductPath(this.tabName);
      public static productCreated = new ProductCreatedPath(this.tabName);

      public static editBundle = new EditBundlePath(this.tabName);
      public static editBundleCompleted = new EditBundleCompletedPath(
        this.tabName
      );
    };
  };

  public static ticketsGroup = class {
    public static groupName = TabGroup.TICKETS;

    public static scannerTab = class {
      static readonly tabName = Tabs.SCANNER;

      public static root = new TicketScannerPath(this.tabName);
    };

    public static ticketsTab = class {
      static readonly tabName = Tabs.TICKETS;
      public static root = new TicketsPath(this.tabName);
    };
  };
}
